import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Routes and the authentication guard.
 *
 * The guard is a **convenience**, not a control. Every route below is served by the same public
 * API that any other client uses, and the core authorises each request on its own (design §8.2).
 * If this guard were removed, an unauthenticated person would see empty screens and `401`s — not
 * data. That is the correct relationship between a client-side route guard and a security
 * boundary, and it is worth stating because the opposite arrangement is so common.
 */

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    // `/` redirected to the asset list, which answers "what do we monitor" — not a question
    // anybody opens a monitoring system to ask. The home screen answers "what needs me".
    component: () => import('@/views/HomeView.vue'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, chrome: false },
  },
  {
    path: '/assets',
    name: 'assets',
    component: () => import('@/views/AssetsView.vue'),
    meta: { section: 'assets' },
  },
  {
    path: '/assets/:id',
    name: 'asset',
    component: () => import('@/views/AssetDetailView.vue'),
    meta: { section: 'assets' },
  },
  {
    path: '/operations',
    name: 'operations',
    component: () => import('@/views/OperationsView.vue'),
    meta: { section: 'operations' },
  },
  {
    path: '/activity',
    name: 'activity',
    component: () => import('@/views/ActivityView.vue'),
    meta: { section: 'activity' },
  },
  {
    path: '/alerts',
    name: 'alerts',
    component: () => import('@/views/AlertsView.vue'),
    meta: { section: 'alerts' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
]

export const createAppRouter = () => {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
  })

  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    if (to.meta.public === true) {
      return auth.isAuthenticated && to.name === 'login' ? { name: 'home' } : true
    }

    if (auth.isAuthenticated) return true

    // A page reload drops the in-memory access token but keeps the refresh token. Resuming here
    // rather than bouncing to the login screen is the difference between a reload that keeps your
    // place and one that loses it.
    if (auth.canResume && (await auth.resume())) return true

    return { name: 'login', query: to.fullPath === '/' ? {} : { next: to.fullPath } }
  })

  return router
}
